import AxiosInstance from '@/api/axios';
import { ROLE } from '@/utils/enum';
import { getRoleFromLocalStorage } from '@/utils/helper';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ControlCentreDataType } from './controlCentreSlice';

interface IInsightsFilter {
  organization_id: string;
  date_range?: string;
  force?: boolean;
  selected_month_Number?: number;
  selected_year?: string;
}

let abortController: AbortController | null = null;

const checkParamsChanges = (
  oldParams: IInsightsFilter,
  newParams: IInsightsFilter
) => {
  return (
    oldParams.organization_id !== newParams.organization_id ||
    oldParams.date_range !== newParams.date_range
  );
};

export const fetchInsights = createAsyncThunk(
  'insights/fetchData',
  async (
    {
      organization_id,
      date_range,
      force,
      selected_month_Number,
      selected_year,
    }: IInsightsFilter,
    { rejectWithValue, getState }
  ) => {
    const role = getRoleFromLocalStorage();
    if (
      ![ROLE.SUPEROWNER, ROLE.USERKEEPME, ROLE.KEEPME].includes(role as ROLE)
    ) {
      return rejectWithValue('Unauthorized role.');
    }

    abortController = new AbortController();
    try {
      const response = await AxiosInstance.get('/metrics/all-insights', {
        params: {
          organization_id,
          date_range,
          force, // Pass force parameter to backend
          selected_month_Number,
          selected_year,
        },
        signal: abortController.signal,
        timeout: 300000, // 5 minute timeout for production with many organizations
      });

      if (response.data.success) {
        return {
          insights: response.data.data,
          params: { organization_id, date_range },
        };
      }

      return rejectWithValue('Failed to fetch data.');
    } catch (error: any) {
      if (abortController.signal.aborted || error.code === 'ERR_CANCELED') {
        return rejectWithValue('Request was canceled.');
      }

      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return rejectWithValue(
          'Request timed out. The metrics page is processing a large amount of data. Please try:\n' +
            '1. Selecting a smaller date range\n' +
            '2. Filtering by a specific client\n' +
            '3. Refreshing the page and trying again\n\n' +
            'If the issue persists, please contact support.'
        );
      }

      return rejectWithValue(error.message || 'An error occurred.');
    } finally {
      abortController = null;
    }
  }
);

interface IInsightsState {
  insights: ControlCentreDataType | null;
  loading: boolean;
  error: string | null;
  lastFetchedParams: IInsightsFilter;
}

const initialState: IInsightsState = {
  insights: null,
  loading: true,
  error: null,
  lastFetchedParams: {
    organization_id: '',
  },
};

const insightsReducer = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    abortFetch: () => {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.loading = false;

        if ('insights' in action.payload) {
          state.insights = action.payload.insights;
          state.lastFetchedParams = action.payload.params;
        } else {
          state.insights = action.payload;
        }
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { abortFetch } = insightsReducer.actions;

export default insightsReducer.reducer;
